/**
 * ClimateCredentialsPanel — Unified 7-Section Climate & Circularity Credentials
 * Used across: SME, Corporate, Project, States workspaces.
 * Dynamic, auditable, evidence-linked, decision-grade.
 *
 * Props:
 *   data     — credentials response from /api/credentials/{type}
 *   loading  — boolean
 *   onRefresh — function
 */
import React, { useState, useEffect } from "react";
import { Shield, CheckCircle2, AlertTriangle, FileText, BarChart2,
         Lock, RefreshCw, ChevronDown, Send, Download, Clock } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";

const CONF_COLORS = {
  "Finance Ready":      "text-emerald-600 bg-emerald-50 border-emerald-200",
  "High Confidence":    "text-teal-600 bg-teal-50 border-teal-200",
  "Moderate Confidence":"text-amber-600 bg-amber-50 border-amber-200",
  "Low Confidence":     "text-red-600 bg-red-50 border-red-200",
};

const OUTCOME_COLORS = {
  emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
  teal:    "text-teal-700 bg-teal-50 border-teal-200",
  amber:   "text-amber-700 bg-amber-50 border-amber-200",
  red:     "text-red-700 bg-red-50 border-red-200",
  slate:   "text-slate-600 bg-slate-50 border-slate-200",
};

function Section({ title, icon: Icon, color = "text-slate-600", children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-4">
      <button className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50"
        onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className={color} />}
          <p className={`text-[10px] font-bold uppercase tracking-wider ${color}`}>{title}</p>
        </div>
        <ChevronDown size={13} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 pt-2">{children}</div>}
    </div>
  );
}

function KV({ label, value, highlight }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
      <p className="text-[9px] text-slate-500 font-medium">{label}</p>
      <p className={`text-[9px] font-bold ${highlight ? "text-teal-700" : "text-slate-700"} max-w-[55%] text-right`}>
        {value ?? "—"}
      </p>
    </div>
  );
}

function ScopeBar({ scope1, scope2, scope3, total }) {
  if (!total || total <= 0) return null;
  const s1w = scope1 / total * 100; const s2w = scope2 / total * 100; const s3w = scope3 / total * 100;
  return (
    <div className="mt-3">
      <div className="h-4 rounded-full overflow-hidden flex">
        <div className="bg-amber-500" style={{ width: `${s1w}%` }} title={`Scope 1: ${s1w.toFixed(1)}%`} />
        <div className="bg-teal-500"  style={{ width: `${s2w}%` }} title={`Scope 2: ${s2w.toFixed(1)}%`} />
        <div className="bg-blue-400"  style={{ width: `${s3w}%` }} title={`Scope 3: ${s3w.toFixed(1)}%`} />
      </div>
      <div className="flex gap-3 mt-1.5 text-[7px] text-slate-400">
        {[["bg-amber-500","Scope 1",s1w],["bg-teal-500","Scope 2",s2w],["bg-blue-400","Scope 3",s3w]].map(([c,l,p]) => (
          <span key={l} className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-sm ${c}`}/>{l} ({p.toFixed(1)}%)
          </span>
        ))}
      </div>
    </div>
  );
}

export default function ClimateCredentialsPanel({ data, loading, onRefresh, workspace, periodLabel }) {
  const [verifStatus, setVerifStatus] = useState(null);
  const [submitting, setSubmitting]   = useState(false);

  useEffect(() => {
    if (workspace && periodLabel) {
      client.get(`/verification/${workspace}/${periodLabel}/status`)
        .then(r => setVerifStatus(r.data))
        .catch(() => {});
    }
  }, [workspace, periodLabel]);

  const handleSubmitVerification = async () => {
    if (!confirm("Submit this period for verification review?")) return;
    setSubmitting(true);
    try {
      await client.post(`/verification/${workspace}/${periodLabel}/submit`, {
        declaration: "I confirm the data submitted is accurate to the best of my knowledge.",
      });
      const res = await client.get(`/verification/${workspace}/${periodLabel}/status`);
      setVerifStatus(res.data);
      toast.success("Submitted for verification review.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed.");
    }
    setSubmitting(false);
  };

  const handleDownloadCertificate = async () => {
    try {
      const resp = await client.get(
        `/verification/${workspace}/${periodLabel}/certificate`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `DCI-Certificate-${periodLabel}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Certificate generation failed."); }
  };
  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  if (!data) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
      <AlertTriangle size={24} className="text-amber-500 mx-auto mb-3" />
      <p className="text-sm font-bold text-amber-700">No credentials available</p>
      <p className="text-xs text-amber-600 mt-1">Calculate at least one period to generate credentials.</p>
    </div>
  );

  const s1 = data.section_1_summary || {};
  const s2 = data.section_2_methodology || {};
  const s3 = data.section_3_breakdown || {};
  const s4 = data.section_4_data_quality || {};
  const s5 = data.section_5_evidence || {};
  const s6 = data.section_6_risk || {};
  const s7 = data.section_7_decision || {};

  const confClass = CONF_COLORS[s1.confidence_tier] || CONF_COLORS["Low Confidence"];
  const outClass  = OUTCOME_COLORS[s1.decision_color] || OUTCOME_COLORS.slate;

  return (
    <div data-testid="climate-credentials-panel">
      {/* Header */}
      <div className="bg-[#0F172A] rounded-xl p-5 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">
              {data.workspace_type} — Climate & Circularity Credentials
            </p>
            <p className="text-lg font-bold text-white">{data.organization}</p>
            <p className="text-[8px] text-slate-400 mt-0.5">
              {data.sector} · {data.country} {data.period ? `· ${data.period}` : ""}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`text-[8px] font-bold px-2 py-1 rounded-full border ${confClass}`}>
              {s1.confidence_tier}
            </span>
            {data.is_locked && (
              <span className="flex items-center gap-1 text-[7px] text-emerald-600 font-bold">
                <Lock size={9} /> Locked
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-4">
          {[
            ["TOTAL",   `${(s1.total_tco2e || 0).toFixed(2)} tCO2e`, "text-teal-400"],
            ["SCOPE 1", `${(s1.scope1_tco2e || 0).toFixed(3)}`, "text-amber-400"],
            ["SCOPE 2", `${(s1.scope2_tco2e || 0).toFixed(3)}`, "text-teal-300"],
            ["SCOPE 3", `${(s1.scope3_tco2e || 0).toFixed(3)}`, "text-blue-400"],
          ].map(([l, v, c]) => (
            <div key={l} className="bg-white/5 rounded-xl px-3 py-2 text-center">
              <p className={`text-base font-bold ${c}`}>{v}</p>
              <p className="text-[7px] text-slate-400">{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Section 1: Summary */}
      <Section title="1. Credentials Summary" icon={Shield} color="text-teal-600">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <KV label="Total Emissions" value={`${(s1.total_tco2e || 0).toFixed(2)} tCO2e`} highlight />
            <KV label="Confidence Score"  value={`${s1.confidence_score || "—"}/100`} />
            <KV label="Confidence Tier"   value={s1.confidence_tier} />
          </div>
          <div>
            <KV label="Period"       value={data.period || "Point-in-time"} />
            <KV label="Decision"     value={s1.decision_outcome} />
            {s1.intensity_metrics && Object.keys(s1.intensity_metrics).length > 0 && (
              <KV label="tCO2e/FTE" value={(s1.intensity_metrics.tco2e_per_fte || s1.intensity_metrics.tco2e_per_employee || 0).toFixed(4)} />
            )}
          </div>
        </div>
        <ScopeBar scope1={s1.scope1_tco2e||0} scope2={s1.scope2_tco2e||0}
                  scope3={s1.scope3_tco2e||0} total={s1.total_tco2e||0} />
      </Section>

      {/* Section 2: Methodology */}
      <Section title="2. Methodology & Standards Alignment" icon={FileText} color="text-slate-500">
        <KV label="Primary Standard"    value={s2.primary_standard} />
        <KV label="Boundary Approach"   value={s2.scope_definition} />
        <KV label="Scope 2 Method"      value={s2.scope2_method} />
        <KV label="Methodology Version" value={data.methodology_version} />
        <KV label="Calculation Hash"    value={data.calculation_hash ? data.calculation_hash.slice(0,16) + "..." : "—"} />
        <div className="flex gap-1.5 flex-wrap mt-3">
          {["GHG Protocol", s2.ifrs_s2_aligned && "IFRS S2", s2.tcfd_aligned && "TCFD"]
            .filter(Boolean).map(fw => (
            <span key={fw} className="text-[7px] font-bold px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full border border-teal-200">
              {fw}
            </span>
          ))}
        </div>
      </Section>

      {/* Section 3: Emissions Breakdown */}
      <Section title="3. Emissions & Circularity Breakdown" icon={BarChart2} color="text-amber-600">
        <KV label="Scope 1 (Direct)"         value={`${(s3.scope1_tco2e||0).toFixed(4)} tCO2e (${s3.scope1_share_pct||0}%)`} />
        <KV label="Scope 2 (Purchased Energy)"value={`${(s3.scope2_tco2e||0).toFixed(4)} tCO2e (${s3.scope2_share_pct||0}%)`} />
        <KV label="Scope 3 (Value Chain)"     value={`${(s3.scope3_tco2e||0).toFixed(4)} tCO2e (${s3.scope3_share_pct||0}%)`} />
        <KV label="Total"                     value={`${(s3.total_tco2e||0).toFixed(4)} tCO2e`} highlight />
      </Section>

      {/* Section 3B: Calculation Trace — KEY TRACEABILITY LAYER */}
      {data.section_3b_calculation_trace?.traces?.length > 0 && (
        <Section title="3B. Calculation Trace — Input → Factor → Output" icon={FileText} color="text-blue-600">
          <p className="text-[8px] text-slate-500 mb-3">{data.section_3b_calculation_trace.methodology_note}</p>
          <div className="space-y-2">
            {data.section_3b_calculation_trace.traces.map((trace, i) => (
              <div key={i} className={`rounded-xl border p-3 ${
                trace.scope === 1 ? "border-amber-100 bg-amber-50/50" :
                trace.scope === 2 ? "border-teal-100 bg-teal-50/50" :
                "border-blue-100 bg-blue-50/50"
              }`} data-testid={`trace-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${
                      trace.scope === 1 ? "bg-amber-100 text-amber-700" :
                      trace.scope === 2 ? "bg-teal-100 text-teal-700" : "bg-blue-100 text-blue-700"
                    }`}>S{trace.scope}</span>
                    <p className="text-[9px] font-bold text-slate-700">{trace.line_item}</p>
                  </div>
                  <p className="text-[9px] font-bold text-teal-700">{trace.result_tco2e?.toFixed(4)} tCO2e</p>
                </div>
                <div className="grid grid-cols-2 gap-1 text-[7px] text-slate-500">
                  <div><span className="font-semibold">Input:</span> {trace.input_value}</div>
                  <div><span className="font-semibold">Factor:</span> {trace.emission_factor}</div>
                  <div className="col-span-2"><span className="font-semibold">Formula:</span> {trace.formula}</div>
                  <div className="col-span-2"><span className="font-semibold">Source:</span> {trace.factor_source} ({trace.factor_year})</div>
                  <div className="col-span-2"><span className="font-semibold">Evidence:</span> {trace.input_source}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 bg-slate-50 rounded-xl px-3 py-2">
            <p className="text-[8px] text-slate-500 font-medium">{data.section_3b_calculation_trace.reconciliation_note}</p>
          </div>
        </Section>
      )}

      {/* Section 4: Data Quality */}
      <Section title="4. Data Quality & Confidence" icon={Shield} color="text-blue-600">
        <KV label="Confidence Score"      value={`${s4.confidence_score||0}/100`} highlight />
        <KV label="Confidence Tier"       value={s4.confidence_tier} />
        <KV label="Evidence Coverage"     value={`${(s4.evidence_coverage_pct||0).toFixed(0)}%`} />
        <KV label="Critical Anomalies"    value={`${s4.critical_anomalies||0}`} />
        <KV label="Period Locked"         value={s4.period_locked ? "YES — Tamper-evident" : "NO — Draft"} />
      </Section>

      {/* Section 5: Evidence & Verification */}
      <Section title="5. Evidence & Verification" icon={CheckCircle2} color="text-emerald-600">
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3">
          <p className="text-[8px] font-bold text-amber-700">{s5.verification_status}</p>
        </div>
        <KV label="Hash"         value={data.calculation_hash ? data.calculation_hash.slice(0,20) + "..." : "—"} />
        <KV label="Lock Status"  value={s5.lock_status} />
        {s5.vvb_required_for?.length > 0 && (
          <div className="mt-2">
            <p className="text-[8px] font-bold text-slate-500 mb-1">VVB validation required for:</p>
            {s5.vvb_required_for.map((r, i) => (
              <p key={i} className="text-[7px] text-slate-400">• {r}</p>
            ))}
          </div>
        )}
      </Section>

      {/* Section 6: Risk & Limitations */}
      <Section title="6. Risk & Limitations" icon={AlertTriangle} color="text-red-500">
        {s6.anomaly_flags?.length > 0 ? (
          s6.anomaly_flags.slice(0,3).map((a, i) => (
            <div key={i} className="text-[8px] text-amber-700 bg-amber-50 rounded px-2 py-1 mb-1">
              [{a.severity?.toUpperCase()}] {a.description?.slice(0,80)}
            </div>
          ))
        ) : (
          <p className="text-[8px] text-emerald-600">No anomalies detected.</p>
        )}
        <p className="text-[7px] text-slate-400 italic mt-2">{s6.uncertainty_note}</p>
        {s6.limitations?.map((l, i) => (
          <p key={i} className="text-[7px] text-slate-400 mt-1">• {l}</p>
        ))}
      </Section>

      {/* Section 7: Decision & Readiness */}
      <Section title="7. Decision & Readiness" icon={CheckCircle2} color="text-teal-600">
        {s7.decision?.outcome_label && (
          <div className={`rounded-xl px-3 py-2 mb-3 border ${outClass}`}>
            <p className="text-[9px] font-bold">{s7.decision.outcome_label}</p>
            <p className="text-[8px] mt-0.5">{s7.decision.description?.slice(0,100)}</p>
          </div>
        )}
        <KV label="Institutional Readiness" value={s7.institutional_readiness} highlight />
        <KV label="Confidence Tier"         value={s7.confidence_tier} />
      </Section>

      {/* Verification Status */}
      {workspace && periodLabel && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="verification-status">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Shield size={14} className="text-blue-500" />
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Verification Status</p>
            </div>
            {verifStatus && (
              <span className={`text-[8px] font-bold px-2 py-0.5 rounded-full border ${
                verifStatus.verification_status === "verified"     ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                verifStatus.verification_status === "under_review" ? "bg-blue-100 text-blue-700 border-blue-200"         :
                verifStatus.verification_status === "submitted"    ? "bg-amber-100 text-amber-700 border-amber-200"      :
                verifStatus.verification_status === "rejected"     ? "bg-red-100 text-red-700 border-red-200"            :
                "bg-slate-100 text-slate-600 border-slate-200"
              }`} data-testid="verification-badge">
                {verifStatus?.verification_label || "Draft"}
              </span>
            )}
          </div>

          {/* Approval log */}
          {verifStatus?.approval_log?.length > 0 && (
            <div className="mb-3">
              {verifStatus.approval_log.slice(-3).map((entry, i) => (
                <div key={i} className="flex items-center gap-2 py-1 border-b border-slate-50">
                  <Clock size={9} className="text-slate-400 shrink-0" />
                  <p className="text-[7px] text-slate-400">
                    {entry.at?.slice(0,16)} · <span className="font-bold text-slate-600">{entry.action?.toUpperCase()}</span> by {entry.by}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Verifier comments */}
          {verifStatus?.comments?.length > 0 && (
            <div className="mb-3 space-y-1">
              {verifStatus.comments.map((c, i) => (
                <div key={i} className={`text-[8px] px-2 py-1.5 rounded-lg ${
                  c.severity === "critical" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
                }`}>
                  <span className="font-bold">[{c.severity?.toUpperCase()}] {c.section}</span>: {c.comment}
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            {(!verifStatus || verifStatus.verification_status === "draft" || verifStatus.verification_status === "rejected") && data?.is_locked && (
              <button onClick={handleSubmitVerification} disabled={submitting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-[9px] font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50"
                data-testid="submit-verification-btn">
                <Send size={10} /> {submitting ? "Submitting..." : "Submit for Verification"}
              </button>
            )}
            {(!verifStatus || verifStatus.verification_status !== "draft") && (
              <button onClick={handleDownloadCertificate}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 text-[9px] font-bold rounded-xl hover:bg-slate-50"
                data-testid="download-certificate-btn">
                <Download size={10} /> Certificate PDF
              </button>
            )}
          </div>
          {!data?.is_locked && (
            <p className="text-[7px] text-amber-600 mt-2 italic">Lock this period first to submit for verification.</p>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
        <p className="text-[7px] text-slate-400 italic leading-relaxed">{data.disclaimer}</p>
      </div>

      {/* Output Framing — What this means / Recommended next step */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">What This Means</p>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-slate-50 rounded-xl px-3 py-2.5">
            <p className="text-[8px] font-bold text-slate-500 mb-1">What was calculated</p>
            <p className="text-[8px] text-slate-500">
              {data.workspace_type} emissions for {data.period || "reporting period"}.
              {(data.section_1_summary?.total_tco2e || 0).toFixed(2)} tCO2e across
              Scope 1 ({(data.section_1_summary?.scope1_tco2e || 0).toFixed(3)}),
              Scope 2 ({(data.section_1_summary?.scope2_tco2e || 0).toFixed(3)}),
              Scope 3 ({(data.section_1_summary?.scope3_tco2e || 0).toFixed(3)}).
            </p>
          </div>
          <div className="bg-teal-50 rounded-xl px-3 py-2.5">
            <p className="text-[8px] font-bold text-teal-600 mb-1">Why it matters</p>
            <p className="text-[8px] text-slate-500">
              These credentials are traceable to IPCC 2006 emission factors and verified by DeCarbonIQ.
              They can be used for internal reporting, board disclosure, and financing applications.
            </p>
          </div>
          <div className="bg-blue-50 rounded-xl px-3 py-2.5">
            <p className="text-[8px] font-bold text-blue-600 mb-1">Recommended next step</p>
            <p className="text-[8px] text-slate-500">
              {(data.section_1_summary?.confidence_score || 0) >= 75
                ? "Submit for third-party VVB verification to use in regulatory filings or carbon registries."
                : "Improve data quality (confidence ≥75) then submit for verification to enable external use."}
            </p>
          </div>
        </div>
      </div>

      {onRefresh && (
        <button onClick={onRefresh}
          className="mt-3 flex items-center gap-1.5 text-[9px] text-slate-500 hover:text-teal-600">
          <RefreshCw size={10} /> Refresh credentials
        </button>
      )}
    </div>
  );
}
